import { useEffect, useRef } from 'react'
import { Button, Modal, Text } from '@mantine/core'
import type { BriefingItem } from '../../hooks/useBriefingDay'
import { hiddenLabel, KIND_LABELS } from './briefingCopy'

type BriefingHiddenModalProps = {
  opened: boolean
  onClose: () => void
  items: BriefingItem[]
  onRestore: (item: BriefingItem) => void
  // Closed with nothing hidden: the trigger has unmounted, so the caller places focus.
  onClosedEmpty: () => void
}

/** Every dismissed or reminded item of today's briefing, each with a way back. */
export function BriefingHiddenModal({
  opened,
  onClose,
  items,
  onRestore,
  onClosedEmpty,
}: BriefingHiddenModalProps) {
  // A restored row unmounts with its button: focus the next row's button, else the close button.
  const refocus = useRef<{ goneId: string; nextId: string | undefined; dialog: Element } | null>(
    null,
  )

  useEffect(() => {
    const pending = refocus.current
    refocus.current = null
    if (!pending || items.some((item) => item.id === pending.goneId)) return
    const next = pending.nextId
      ? pending.dialog.querySelector<HTMLElement>(
          `[data-briefing-restore="${CSS.escape(pending.nextId)}"]`,
        )
      : null
    ;(next ?? pending.dialog.querySelector<HTMLElement>('button[aria-label="Close"]'))?.focus()
  }, [items])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Dismissed & reminders"
      size="lg"
      closeButtonProps={{ 'aria-label': 'Close' }}
      returnFocus={items.length > 0}
      onExitTransitionEnd={() => {
        if (items.length === 0) onClosedEmpty()
      }}
    >
      {items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          Nothing is hidden.
        </Text>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100">
          {items.map((item, index) => (
            <li key={item.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <Text size="sm" fw={600} truncate>
                  {item.patient.name ?? 'Unknown patient'} · {KIND_LABELS[item.kind]}
                </Text>
                <Text size="sm" truncate title={item.title}>
                  {item.title}
                </Text>
                {item.state && (
                  <Text size="xs" c="gray.7">
                    {hiddenLabel(item.state)}
                  </Text>
                )}
              </div>
              <Button
                variant="default"
                size="xs"
                data-briefing-restore={item.id}
                onClick={(event) => {
                  const dialog = event.currentTarget.closest('[role="dialog"]')
                  if (dialog) {
                    const nextId = (items[index + 1] ?? items[index - 1])?.id
                    refocus.current = { goneId: item.id, nextId, dialog }
                  }
                  onRestore(item)
                }}
              >
                Restore to briefing
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
